import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { initializeAnalytics, logPageView } from './analytics';
import { captureAdParams } from './utils/adTracking';
import { AuthProvider } from './components/AuthContext';
import SideBar from './components/SideBar';
import CreateHome from './components/CreateHome';
import TattooIdeasPage from './components/TattooIdeasPage';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import TopNavigation from './components/TopNavigation';
import PhoneNavigation from './components/PhoneNavigation';
import { useMediaQuery } from 'react-responsive';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Route-level code splitting: everything below is lazy-loaded so the initial
// bundle stays light. CreateHome itself is eager (the '/' landing sections
// paint immediately); it lazy-loads the Babylon-heavy CreateCanvas internally.
const Home = lazy(() => import('./components/Home'));
const UserPage = lazy(() => import('./components/UserPage'));
const SubscriptionPage = lazy(() => import('./components/SubscriptionPage'));
const Settings = lazy(() => import('./components/Settings'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const SupportPage = lazy(() => import('./components/SupportPage'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const SuccessPage = lazy(() => import('./components/SuccessPage'));
const ArtistPage = lazy(() => import('./components/ArtistPage'));
const MyShop = lazy(() => import('./components/MyShop'));
const CreateArtist = lazy(() => import('./components/CreateArtist'));
const ArtistFeed = lazy(() => import('./components/ArtistFeed'));
const BookingPage = lazy(() => import('./components/BookingPage'));
const BookingSuccess = lazy(() => import('./components/BookingSuccess'));
const About = lazy(() => import('./components/About'));
const TermsPage = lazy(() => import('./components/TermsPage'));
const PrivacyPage = lazy(() => import('./components/PrivacyPage'));
const RefundPolicyPage = lazy(() => import('./components/RefundPolicyPage'));
const StyleFeed = lazy(() => import('./components/StyleFeed'));

// Shown briefly while a lazy route chunk downloads.
const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-gray-800">
    <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/20 border-t-rose-500" />
  </div>
);

// Load Stripe with error handling to prevent app crashes
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY).catch(err => {
      console.warn('Failed to load Stripe.js:', err);
      return null; // Return null if Stripe fails to load (e.g., network issue, ad blocker)
    })
  : Promise.resolve(null); // Return resolved null promise if no key

const AnalyticsWrapper = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    logPageView(location.pathname + location.search);
  }, [location]);

  return children;
};

const App = () => {
  const isPhone = useMediaQuery({ query: '(max-width: 768px)' });
  const location = useLocation();

  // Programmatic /tattoo-ideas/* pages are full-bleed with their own header —
  // hide the in-app chrome there. '/' is the create tool now, so it gets the
  // normal app chrome like /create always did.
  const isMarketing = location.pathname.startsWith('/tattoo-ideas');

  useEffect(() => {
    captureAdParams(); // Capture gclid/UTM params on first load
    initializeAnalytics();
  }, []);

  return (
    <AuthProvider>
      <Elements stripe={stripePromise}>
        {!isMarketing && <SideBar />}
        {!isMarketing && (isPhone ? <PhoneNavigation /> : <TopNavigation />)}
        <div className={!isMarketing && isPhone ? 'pt-16' : ''}>
          <AnalyticsWrapper>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* The create tool lives at '/' now; the edge/server also 301s
                  /create → / so crawlers consolidate onto the root URL. */}
              <Route path="/create" element={<Navigate to="/" replace />} />
              <Route path="/tattoo/:tattooId" element={<Home />} />
              <Route path="/" element={<CreateHome />} />
              <Route path="/tattoo-ideas/:slug" element={<TattooIdeasPage />} />
              <Route path="/tattoo-ideas/:slug/:placementSlug" element={<TattooIdeasPage />} />
              <Route path="/browse" element={<Home />} />
              <Route path="/discover" element={<Navigate to="/browse" replace />} />
              <Route path="/style/:styleSlug" element={<StyleFeed />} />
              <Route path="/userpage/:username" element={<UserPage />} />
              <Route path="/userpage/:username/:tattooId" element={<UserPage />} />
              <Route path="/subscribe" element={<SubscriptionPage />} />
              <Route path="/settings" element={<Settings />} />
              {/* Messaging has been removed - redirect any old links to Create */}
              <Route path="/messages/:conversationId" element={<Navigate to="/" replace />} />
              <Route path="/messages" element={<Navigate to="/" replace />} />
              <Route path="/booking-success" element={<BookingSuccess />} />
              <Route path="/admin" element={<Dashboard />} />
              <Route path="/artists/:artistId" element={<ArtistPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/booking/:artistId" element={<BookingPage />} />
              <Route path="/createartist" element={<CreateArtist />} />
              <Route path="/myshop/:artistId" element={<MyShop />} />
              <Route path="/search/:locationSlug" element={<ArtistFeed />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/auth/reset-password/:token" element={<ResetPassword />} />
              <Route path="/success" element={<SuccessPage />} />
            </Routes>
            </Suspense>
          </AnalyticsWrapper>
        </div>
      </Elements>
    </AuthProvider>
  );
};

export default App;